/* ─────────────────────────────────────────────────────────────────────────────
   DOCUMENTARY SEED RECORDS — SOURCED HISTORY, NOT ORAL HISTORY.

   These exist so that `npm run dev` on a fresh clone shows a working map before
   any backend is connected.

   An earlier version of this file held invented stories with invented narrators.
   That was a mistake and it has been replaced. Every record below is drawn from
   a published source, cited in `source_note`, with coordinates resolved through
   the Lands Department Address Lookup Service or the Housing Authority's own
   dataset rather than estimated by eye. Nothing here is attributed to a person.
   There are no quotations from informants, and no `narrator_label`, because none
   of this came from an interview — it came from censuses, newspapers, archives
   and departmental reports.

   That distinction is the whole point of the project. A record on this map is
   either testimony someone gave, or documentation someone published. Blurring
   the two would let the map launder invention into evidence.

   Three findings changed what is in this file, and are worth carrying into the
   research itself:

     · The administrative category was "boat squatters", not 蜑家. Colonial
       records, Hansard and Housing Department papers index the former. Smart &
       Fung (2023) show boat squatters were EXCLUDED from the 1984–85 Squatter
       Occupancy Survey — the registration that gated permanent public housing.
     · The archives split on orthography. The Public Records Office indexes 蛋家
       and returns zero for 蜑家; Hong Kong Memory is the exact inverse. Search
       both graphs at both institutions or you will get a false negative.
     · A plausible-looking lead was falsified during sourcing: 茶果嶺 and 鯉魚門
       are Hakka stone-quarrying villages, not boat-dweller settlements. A record
       for 茶果嶺 was drafted here and then deleted. That deletion is the file
       working as intended.

   Loaded ONLY when VITE_SUPABASE_URL is unset. Once a real backend is
   configured this file is never read, so seed records cannot leak into the
   research corpus. Every row carries `sample: true`.

   If you fork this project for a different place, replace this file with your
   own sourced records or ship an empty map. Do not invent testimony.
   ───────────────────────────────────────────────────────────────────────────*/

const DOC = ' ｜ 文獻記錄，非口述訪談。Documentary record, not an interview.'

export const SAMPLE_STORIES = [
  {
    id: 'sample-1',
    kind: 'relocation',
    title_zh: '漁光村：上樓的條件是賣掉漁船',
    title_en: 'Yue Kwong Chuen: the flat was conditional on selling the boat',
    narrative_zh:
      '香港房屋協會的檔案直述興建目的：「為香港仔沿岸及水上居民，' +
      '以及鴨脷洲的居民提供廉租房屋」。五座樓宇、一千一百四十四個單位，' +
      '一九六二、六三、六五年分批落成。' +
      '值得留意的是入住條件：有記載指「房屋經理說漁船是資產，' +
      '要求他變賣漁船才批准申請」。' +
      '換言之，安置的機制同時是一種去海洋化 —— ' +
      '上樓不只是換一個住處，而是要求放棄生產工具。' +
      '一九六二年香港仔岸上人口一萬六千六百九十，船上人口二萬七千四百七十九。',
    narrative_en:
      "The Housing Society's own archive states the purpose plainly: low-rental " +
      'housing "to the residents along the coast and boat people in Aberdeen and Ap Lei ' +
      'Chau." Five blocks, 1,144 units, completed in batches in 1962, 1963 and 1965. ' +
      'The condition of entry is the part that matters: a housing manager is recorded ' +
      'as treating the fishing boat as an asset and requiring it be sold before the ' +
      'application would be approved. Rehousing here operated as a mechanism of ' +
      'de-maritimisation, not merely shelter — moving ashore meant surrendering the ' +
      'means of production. In 1962 Aberdeen counted 16,690 people ashore and 27,479 ' +
      'afloat.',
    communities: ['tanka', 'sheungan'],
    year_start: 1962, year_end: 1965,
    origin_lng: 114.1500, origin_lat: 22.2455,   // Aberdeen Harbour / 香港仔避風塘
    dest_lng: 114.15686, dest_lat: 22.25085,     // 順風樓, 香港仔水塘道 (LandsD ALS)
    place_zh: '香港仔避風塘', place_en: 'Aberdeen Harbour',
    dest_place_zh: '漁光村', dest_place_en: 'Yue Kwong Chuen',
    precision_level: 'approximate',
    source_note:
      '香港房屋協會房屋檔案 hkhs.com/tc/housing_archive/id/30；房屋局文字紀錄 '
      + 'hb.gov.hk/tc/policy/housing/policy/transitionalhousing/d1_ts.pdf；'
      + 'Barber & Chung, "Boat dwellers and maritime heritage in Hong Kong", '
      + 'Int. J. Heritage Studies 29(11) 2023, DOI 10.1080/13527258.2023.2244918；'
      + '賣船條件及人口數字見 Urban Diary（南區區議會／衛奕信勳爵文物信託夥伴計劃）。'
      + '座標取自地政總署地址查詢服務。' + DOC,
    license: 'CC-BY-SA-4.0',
    sample: true,
  },

  {
    id: 'sample-2',
    kind: 'relocation',
    title_zh: '石排灣邨：先失火，後填海，最後上樓',
    title_en: 'Shek Pai Wan Estate: fire, then reclamation, then the flat',
    narrative_zh:
      '一九六〇年初春，香港仔涌尾一帶艇戶發生大火。' +
      '約一九六七年，政府決定在該處填海；石排灣邨七座第四型徙置大廈' +
      '於一九六六至六八年間落成，記載為「主要用作安置涌尾的艇戶，' +
      '以騰出水域作填海之用」。安置不是目的，而是填海的前置工序。' +
      '一九六〇年版《香港地名志》仍將石排灣列為海港；今日該處已是陸地。' +
      '⚠ 未解的分歧：另有記載指涌尾艇戶「分批遷往鄰近的田灣邨和石排灣邨」，' +
      '田灣邨可能是一個被忽略的第二安置點。這條記錄保留分歧，不作調和。',
    narrative_en:
      'In early spring 1960 a fire swept the boat households at Chung Mei, Aberdeen. ' +
      'Around 1967 the government decided to reclaim the site. Seven Mark IV ' +
      'resettlement blocks were completed at Shek Pai Wan between 1966 and 1968, ' +
      'recorded as built "principally to rehouse the boat households at Chung Mei, so ' +
      'as to free the water for reclamation." The rehousing was not the objective; it ' +
      'was the step that had to happen before the bay could be filled. The 1960 ' +
      'Gazetteer still classifies Shek Pai Wan as a harbour. It is now land. ' +
      '⚠ Unresolved: one account has the Chung Mei households moved in batches to Tin ' +
      'Wan Estate AND Shek Pai Wan Estate. Tin Wan may be an under-documented second ' +
      'receiving estate. The discrepancy is preserved here rather than smoothed away.',
    communities: ['tanka', 'sheungan'],
    year_start: 1966, year_end: 1968,
    origin_lng: 114.1534, origin_lat: 22.2460,   // 涌尾 / 石排灣, a harbour in 1960
    dest_lng: 114.15726, dest_lat: 22.24933,     // 石排灣邨 (Housing Authority)
    place_zh: '涌尾（一九六〇年志書列石排灣為海港）',
    place_en: 'Chung Mei (Shek Pai Wan, a harbour in the 1960 Gazetteer)',
    dest_place_zh: '石排灣邨', dest_place_en: 'Shek Pai Wan Estate',
    precision_level: 'approximate',
    source_note:
      '〈石排灣新區首座徙廈建成　涌尾住家艇居民下月將徙置入住〉，《香港工商日報》1966年10月18日；'
      + '劉智鵬、黃君健《黃竹坑故事：從河谷平原到創協坊》三聯書店 2015，頁13（一九六〇年大火）；'
      + '田灣邨分歧見 Urban Diary。⚠ 已核實的否定結果：房委會「公屋發展歷程」及'
      + '香港文化博物館公屋五十年專刊均無記載此事，勿引政府網頁為證。'
      + '座標取自房委會 prh-estates.json 及地政總署《香港地名志》。' + DOC,
    license: 'CC-BY-SA-4.0',
    sample: true,
  },

  {
    id: 'sample-3',
    kind: 'relocation',
    title_zh: '一九七六年愛秩序灣大火',
    title_en: 'The 1976 Aldrich Bay fire',
    narrative_zh:
      '一九七六年二月一日（年初二），筲箕灣愛秩序灣岸邊寮屋區發生五級大火，' +
      '木屋與艇屋同遭焚毀，三千二百二十一人無家可歸，十二人受傷，無人死亡。' +
      '英軍在鯉魚門軍營設帳篷收容災民。' +
      '興華邨裕興樓及豐興樓提早六星期落成以安置災民；' +
      '房委會資料顯示興華二邨首次入伙年份正是一九七六年。' +
      '⚠ 用詞須準確：中文報導慣稱「艇戶大火」，但起火處是岸邊的木屋／艇屋混合社區，' +
      '而非避風塘內泊碇的船隻。寫作時宜稱「沿岸寮屋社區」。' +
      '興華二邨是碩果僅存的第七型徙置大廈 —— 由徙置事務處規劃的最後一批。',
    narrative_en:
      'On 1 February 1976, the second day of Lunar New Year, a No. 5 alarm fire tore ' +
      'through the shoreline squatter settlement beside Aldrich Bay, Shau Kei Wan, ' +
      'destroying both timber huts and boat-houses. 3,221 people were displaced, 12 ' +
      'injured, none killed. British forces tented the survivors at Lyemun Barracks. ' +
      'Yue Hing House and Fung Hing House at Hing Wah Estate were completed six weeks ' +
      "ahead of schedule to take them in; the Housing Authority's dataset records first " +
      'intake at Hing Wah (2) Estate in 1976. ' +
      '⚠ Be precise: Chinese reporting calls this 艇戶大火, but the fire began in a ' +
      'mixed hut-and-boat-house community on the shore, not among vessels at anchor. ' +
      'Write it as a coastal squatter settlement. Hing Wah (2) holds the last surviving ' +
      'Mark VII blocks — the final generation planned by the Resettlement Department.',
    communities: ['tanka', 'sheungan', 'hoklo'],
    year_start: 1976, year_end: 1976,
    origin_lng: 114.22723, origin_lat: 22.28008,  // 愛秩序灣 (LandsD ALS)
    dest_lng: 114.23359, dest_lat: 22.26236,      // 興華二邨 (Housing Authority)
    place_zh: '愛秩序灣（筲箕灣）', place_en: 'Aldrich Bay, Shau Kei Wan',
    dest_place_zh: '興華二邨', dest_place_en: 'Hing Wah (2) Estate',
    precision_level: 'approximate',
    source_note:
      '香港房屋協會明華大廈口述歷史計劃 hshousingstory.net（一手，糾正「艇戶」框架）；'
      + '何耀生《集體回憶之維多利亞港》明報出版社（提早六周落成）；'
      + '傷亡數字見《香港火災列表》。入伙年份及座標取自房委會 prh-estates.json，'
      + '愛秩序灣座標取自地政總署地址查詢服務。'
      + '⚠ 未解：一九九〇年代愛秩序灣填海後，餘下艇戶遷往何處，暫無資料。' + DOC,
    license: 'CC-BY-SA-4.0',
    sample: true,
  },

  {
    id: 'sample-4',
    kind: 'relocation',
    title_zh: '三聖邨：交還船牌，換取上樓',
    title_en: 'Sam Shing Estate: hand in the licence, get the flat',
    narrative_zh:
      '一九七一年起青山灣填海造地，「不少漁民交還船牌，換取上樓居住，' +
      '浮家泛宅的風貌逐漸褪色」。漁船先遷至三聖灣對出海面；' +
      '再一次填海之後，政府於一九八〇年在青山公路旁的填海區興建三聖邨安置漁民。' +
      '屋邨各座樓宇命名均與漁業有關 —— ' +
      '這是少數在名字上仍保留原居生計的安置屋邨，' +
      '也是同一機制最清晰的一次表述：船牌換樓契。',
    narrative_en:
      'From 1971 reclamation at Castle Peak Bay pushed the fishing fleet out. As one ' +
      'account puts it, many fishermen surrendered their boat licences in exchange for ' +
      'a flat, and the floating settlement faded. The boats moved first to the water ' +
      'off Sam Shing Wan; after a further round of reclamation the government built Sam ' +
      'Shing Estate in 1980, on reclaimed land beside Castle Peak Road, to rehouse ' +
      'them. Every block is named after something to do with fishing — one of the few ' +
      'resettlement estates whose names still carry the livelihood it displaced, and ' +
      'the clearest statement of the mechanism: a boat licence traded for a tenancy.',
    communities: ['tanka', 'sheungan', 'hoklo'],
    year_start: 1971, year_end: 1980,
    origin_lng: 113.9792, origin_lat: 22.3827,   // 三聖灣 / 青山灣
    dest_lng: 113.97811, dest_lat: 22.38107,     // 三聖邨 (Housing Authority)
    place_zh: '青山灣 / 三聖灣', place_en: 'Castle Peak Bay / Sam Shing Wan',
    dest_place_zh: '三聖邨', dest_place_en: 'Sam Shing Estate',
    precision_level: 'approximate',
    source_note:
      '陳天權〈屯門青山灣的演變〉，灼見名家 master-insight.com；'
      + '座標取自房委會 prh-estates.json 及地政總署《香港地名志》。' + DOC,
    license: 'CC-BY-SA-4.0',
    sample: true,
  },

  {
    id: 'sample-5',
    kind: 'relocation',
    title_zh: '萬宜漁村：先登記的三十戶有屋，其餘只有六千元',
    title_en: 'Man Yee Fishing Village: the first thirty got houses, the rest got $6,000',
    narrative_zh:
      '一九六〇年代封閉官門水道興建萬宜水庫，' +
      '「本在水道作業約二百多隻船的漁民統統被要求上岸」。' +
      '首批已登記的約三十戶獲六千元津貼並獲安置於官門漁村（一九七四年，石屋約三十間）；' +
      '其餘被列為流動作業，同樣獲六千元，但沒有房屋。' +
      '「當時大家認為政府在分化他們。」' +
      '一百七十餘戶後於一九七九年遷入萬宜漁村。' +
      '關鍵在於：漁民不具原居民身分，因此最初不在房屋補償之列 —— ' +
      '賠償的分界線不是損失多少，而是登記與否。',
    narrative_en:
      'The damming of Kwun Mun channel for the High Island Reservoir in the 1960s ' +
      'required the roughly 200 boats working those waters to come ashore. The first ' +
      'registered batch — about thirty households — received a $6,000 allowance AND ' +
      'housing: some thirty stone houses at Kwun Mun Fishing Village in 1974. The rest ' +
      'were classed as transient: same $6,000, no house. Residents read this as ' +
      'deliberate division. Around 170 households moved into Man Yee Fishing Village in ' +
      '1979. The underlying point: fishermen held no indigenous-villager status, so ' +
      'housing compensation did not initially apply to them. The line was drawn not by ' +
      'what was lost but by whether you were on a register.',
    communities: ['tanka', 'sheungan'],
    year_start: 1974, year_end: 1979,
    origin_lng: 114.3600, origin_lat: 22.3760,   // 官門水道, dammed for the reservoir
    dest_lng: 114.27057, dest_lat: 22.37591,     // 萬宜漁村, 康健路 (LandsD ALS)
    place_zh: '官門水道（今萬宜水庫）', place_en: 'Kwun Mun Channel (now High Island Reservoir)',
    dest_place_zh: '萬宜漁村', dest_place_en: 'Man Yee Fishing Village',
    precision_level: 'approximate',
    source_note:
      '洪藹婷，香港01社區專題，2016年10月2日 hk01.com/社區專題/45974。'
      + '座標取自地政總署地址查詢服務。⚠ 單一新聞來源，補償細節宜再核。' + DOC,
    license: 'CC-BY-SA-4.0',
    sample: true,
  },

  {
    id: 'sample-6',
    kind: 'place',
    title_zh: '油麻地避風塘：填出來，又被填走',
    title_en: 'Yau Ma Tei Typhoon Shelter: made by filling, unmade by filling',
    narrative_zh:
      '油麻地避風塘一九一六年闢成，方法是削平官涌山、填去淺灘。' +
      '人口崩塌得極快：一九六一年油麻地水上人口三萬二千五百八十三，' +
      '一九六六年只餘八千七百 —— 五年間減少七成三。' +
      '一九七七至七九年，艇戶組織起來爭取上岸。' +
      '一九七九年一月七日，艇戶及支持者乘旅遊車過海遞交請願書時被警方截停，' +
      '七十六人被控非法集會；二月十三日全部罪名成立，' +
      '包括神父、醫生、社工、大專學生在內的十一位支持者被判留案底、簽保守行為十八個月。' +
      '事件於三月七日上達英國上議院。' +
      '⚠ 未解的數字分歧：當年《啟思》年表記六十七人，' +
      '英國國會紀錄及維基百科記五十六人。分歧保留。' +
      '西九龍填海後原避風塘不復存在；今日海事處資料中的名稱是「新油麻地避風塘」—— ' +
      '那個「新」字本身就是紀錄。',
    narrative_en:
      'The shelter was created in 1916 by levelling Kwun Chung Hill and filling the ' +
      'shallows. The population collapsed fast: 32,583 people afloat at Yau Ma Tei in ' +
      '1961, only 8,700 by 1966 — a 73.3% fall in five years. Between 1977 and 1979 the ' +
      'boat households organised to press for housing ashore. On 7 January 1979, ' +
      'travelling by coach to deliver a petition, they were stopped by police and 76 ' +
      'people were charged with unlawful assembly; on 13 February all were convicted, ' +
      'and eleven supporters — a priest, a doctor, social workers, students — were left ' +
      'with criminal records and bound over for eighteen months. The case reached the ' +
      'House of Lords on 7 March 1979. ' +
      '⚠ Unresolved: the contemporaneous 1978–79 chronology counts 67; UK Hansard and ' +
      'Wikipedia say 56. The discrepancy is preserved. ' +
      'West Kowloon reclamation later removed the shelter altogether. The name in the ' +
      "Marine Department's current dataset is \"New Yau Ma Tei Typhoon Shelter\". That " +
      'word "New" is itself the record.',
    communities: ['tanka', 'sheungan'],
    year_start: 1916, year_end: 1996,
    origin_lng: 114.1691, origin_lat: 22.3105,   // 油麻地, 1960 Gazetteer
    place_zh: '油麻地避風塘', place_en: 'Yau Ma Tei Typhoon Shelter',
    precision_level: 'area',
    source_note:
      '彩雲〈從油麻地艇戶爭取上岸行動談起〉及〈艇戶事件年表〉，《啟思》香港大學醫學會 '
      + '1978–79（一手，全文 med.hku.hk/85caduceus/377.pdf 及 /380.pdf）；'
      + 'UK House of Lords, "Hong Kong: Arrest of Petitioners", 7 March 1979, '
      + 'api.parliament.uk/historic-hansard；一九六一及六六年人口見'
      + '政府統計處《1966 By-census》Appendix 8/Table MA；'
      + '名稱見海事處避風塘資料集（CSDI mardep_rcd_1730971403590_9667）。' + DOC,
    license: 'CC-BY-SA-4.0',
    sample: true,
  },

  {
    id: 'sample-7',
    kind: 'place',
    title_zh: '大澳：一九六〇年志書中的六條棚屋村',
    title_en: 'Tai O: six stilt-house villages in the 1960 Gazetteer',
    narrative_zh:
      '地政總署《香港地名志》一九六〇年初版，在大澳之下分列六條村落：' +
      '大澳大涌、二涌、三涌、新基、新沙、沙仔面。' +
      '這是填海與人口外流之前的一份基準名單。' +
      '值得留意志書說不出的東西：它沒有「漁村」這個分類。' +
      '生計不入記錄，只有地名留下來 —— ' +
      '這正是官方資料的限制，也是口述歷史要補的位。',
    narrative_en:
      "The Lands Department's Gazetteer of Place Names, first published 1960, lists six " +
      'settlements under Tai O: Tai O Tai Chung, Yi Chung, Sam Chung, San Kei, San Sha ' +
      'and Sha Tsai Min. It is a baseline taken before reclamation and outward ' +
      'migration. Note what the Gazetteer cannot tell you: it has no category for a ' +
      'fishing village. Livelihood was never recorded — only the place names survive. ' +
      'That is the limit of the official record, and precisely the gap oral history has ' +
      'to fill.',
    communities: ['tanka'],
    year_start: 1960, year_end: 1960,
    origin_lng: 113.8618, origin_lat: 22.2570,
    place_zh: '大澳', place_en: 'Tai O',
    precision_level: 'area',
    source_note:
      '地政總署《香港地名志》（1960年初版），經 CSDI 資料集 '
      + 'landsd_rcd_1648571595120_89752（GEO_PLACE_NAME ⋈ PLACE_NAME 以 GEO_NAME_ID 連接）。'
      + DOC,
    license: 'CC-BY-SA-4.0',
    sample: true,
  },

  {
    id: 'sample-8',
    kind: 'ecology',
    title_zh: '水上人口的消失：十三萬七千 → 五萬九千',
    title_en: 'The vanishing marine population: 136,802 → 59,050',
    narrative_zh:
      '殖民地政府另設「水上人口普查」，特意安排在農曆新年或端午節進行 —— ' +
      '那是全年僅有的、九成船隻同時在港的時刻。' +
      '結果：水上人口一九六一年十三萬六千八百零二，' +
      '一九七一年七萬九千八百九十四，一九七六年五萬九千零五十；' +
      '佔全港人口比例由百分之四點四跌至百分之一點四。' +
      '一九七一年人口普查報告第3.3節自己給出解釋：' +
      '「部分家庭轉業上岸居住，部分住在固定船隻或住家艇的人已被安置入徙置屋邨。」' +
      '——即殖民地政府本身，把水上人口的減少直接歸因於徙置。' +
      '同期土木工程拓展署數字：截至二〇一六年填海造地約六千九百五十四公頃，' +
      '約佔全港土地面積百分之七。' +
      '⚠ 分類警告：一九七一年報告指漁民住戶僅佔水上住戶百分之六十點二 —— ' +
      '約四成水上人口並非漁民。「水上人」不等於「漁民」。',
    narrative_en:
      'The colonial government ran a separate Marine Census, timed to Lunar New Year or ' +
      'the Dragon Boat Festival — the only moments in the year when some 90% of boats ' +
      'were in harbour. It counted a marine population of 136,802 in 1961, 79,894 in ' +
      '1971 and 59,050 in 1976: from 4.4% of the territory to 1.4%. ' +
      'The 1971 Census Main Report, §3.3, gives its own explanation: the decrease was ' +
      'due "partly to the fact that a number of families had changed their occupations ' +
      'and gone to live ashore, and partly because some people living in stationary ' +
      'boats or houseboats had been rehoused in resettlement estates." The colonial ' +
      'state itself, attributing the collapse to resettlement. ' +
      'Over the same span CEDD puts reclaimed land at about 6,954 hectares as at 2016, ' +
      "roughly 7% of Hong Kong's land area. " +
      '⚠ Category warning: the 1971 report records that fisherfolk households were only ' +
      '60.2% of households living on boats. Around 40% of the marine population were ' +
      'not fishermen. 水上人 is not a synonym for 漁民.',
    communities: ['tanka', 'sheungan', 'hoklo', 'punti'],
    year_start: 1961, year_end: 1976,
    origin_lng: 114.1690, origin_lat: 22.2930,
    place_zh: '維多利亞港', place_en: 'Victoria Harbour',
    precision_level: 'area',
    ecology_change:
      '十九世紀海岸線與現今海岸線之間的水域已成陸地；潮間帶棲地大幅消失，'
      + '避風塘體系隨填海重組。 / The water between the nineteenth-century shoreline and '
      + 'the present one is now land; intertidal habitat was lost at scale and the '
      + 'typhoon-shelter system was rebuilt around the reclamation.',
    source_note:
      '政府統計處《1976 By-Census》第八章 The Marine Population, 表8.1；'
      + '《1971 Census Main Report》§3.3 及第七章；《1966 By-census》§16.3；'
      + '全部可於 censtatd.gov.hk 免費下載。'
      + '填海面積見土木工程拓展署 Information Sheet (3), Reclamation in Hong Kong。'
      + '⚠ 資料本身有內部矛盾：一九七六年數字表3.7作59,200，表8.1作59,050。' + DOC,
    license: 'CC-BY-SA-4.0',
    sample: true,
  },
]
